import { useCallback, useEffect, useMemo, useState } from "react";
import { getAdCampaigns, updateCampaignStatus } from "../../../api/ads";
import type { AdCampaign, AdCampaignListParams } from "../../../api/ads";
import { AdsCampaignStatus } from "../../../api/ads";

export const useAdCampaigns = (initialParams: AdCampaignListParams = {}) => {
  const defaultParams: AdCampaignListParams = useMemo(
    () => ({
      pageNum: 1,
      pageSize: 10,
      search: "",
      placement: "ALL",
      pricingModel: "ALL",
      status: "ALL",
      startAt: undefined,
      endAt: undefined,
      ...initialParams,
    }),
    [initialParams]
  );

  const [params, setParamsState] = useState<AdCampaignListParams>(defaultParams);
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCampaigns = useCallback(
    async (override?: Partial<AdCampaignListParams>) => {
      try {
        setListLoading(true);
        setError(null);

        const merged: AdCampaignListParams = { ...params, ...override };
        const response = await getAdCampaigns(merged);
        setCampaigns(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        setError(err?.message || "Failed to load campaigns");
      } finally {
        setListLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const setParams = useCallback((next: Partial<AdCampaignListParams>) => {
    setParamsState((prev) => ({ ...prev, ...next, pageNum: 1 }));
  }, []);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  const updateStatus = useCallback(async (id: string, status: AdsCampaignStatus) => {
    setActionLoading(true);
    setError(null);
    try {
      await updateCampaignStatus(id, status);
      setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch (err: any) {
      setError(err?.message || "Failed to update campaign status");
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    functions: {
      fetchCampaigns,
      setParams,
      setPageNum,
      setPageSize,
      updateStatus,
    },
    values: {
      campaigns,
      params,
      totalCount,
      totalPages,
      error,
    },
    loading: {
      listLoading,
      actionLoading,
    },
  };
};

